/**
 * RequireLauncherApp.jsx — route guard that uses the same app list as the Apps menu.
 * Shaped by: launcher prompt L2; SCL-028, SCL-032, SCL-040; D-42-05, D-42-06.
 * Invariants: access is getAppsForUser, not the painted menu; a failed grant read
 *   does not add apps; a failed FDE flag read does not add flag tiles; signed-out
 *   visitors go to sign-in; denial does not redirect.
 * Related: useLauncherAuthState.js, appCatalog.js, AppRoutes.jsx.
 */
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Alert, Container } from "@mui/material";
import Loading from "components/Loading";
import { useAuth } from "hooks/useAuth";
import { getAppsForUser } from "./appCatalog";
import { useLauncherAuthState } from "./useLauncherAuthState";

export function RequireLauncherApp({ appId, children }) {
  const { user, userLoading } = useAuth();
  const location = useLocation();
  const { authState, loading } = useLauncherAuthState();

  if (userLoading || loading) {
    return <Loading page />;
  }

  if (!user?.uid) {
    // NewsPage sends signed-out visitors to /auth. Other gates keep a return
    // path on that same sign-in route (RequireChartMindIAccess).
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/auth?next=${encodeURIComponent(next)}`} replace />;
  }

  const allowed = getAppsForUser(authState).some((app) => app.id === appId);
  if (!allowed) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info" data-testid="launcher.guard.denied">
          This app is not available for your account.
        </Alert>
      </Container>
    );
  }

  return children;
}

export default RequireLauncherApp;
